import type { RovoInstrumentedError } from '@atlassian/conversation-assistant-instrumentation';
import { FetchError } from '@atlassian/conversation-assistant-service-api';

import {
	type GqlError,
	type GqlResponse,
	type GqlSuccessResponse,
	type GqlVariables,
} from './types';

const extractQueryName = (gqlQuery: string): string | undefined => {
	const operationMatch = gqlQuery.match(/(?:query|mutation)\s+(\w+)/);
	return operationMatch ? operationMatch[1] : undefined;
};

export const performGqlFetch = async <T, U>({
	gqlQuery,
	headerOptions,
	variables,
	abortController,
}: {
	gqlQuery: string;
	headerOptions?: Record<string, string>;
	variables?: GqlVariables<U>;
	abortController?: AbortController;
}): Promise<GqlSuccessResponse<T>> => {
	try {
		const response = await fetch('/gateway/api/graphql', {
			headers: {
				'Content-Type': 'application/json',
				...headerOptions,
			},
			method: 'POST',
			body: JSON.stringify({ query: gqlQuery, variables }),
			signal: abortController?.signal,
		});

		const json: GqlResponse<T> = await response.json();

		if (json?.errors?.length) {
			await processGqlErrorResponse({ errors: json.errors, gqlQuery });
		}
		return json as GqlSuccessResponse<T>;
	} catch (err) {
		// GqlApiError is already processed
		if (err instanceof GqlApiError) {
			throw err;
		}

		// Handle abort signal gracefully, it shouldn't be considered an error despite being caught
		if (err instanceof Error && err.name === 'AbortError') {
			return {
				data: null as unknown as T,
				errors: undefined,
			};
		}
		const originalErrorMessage = err instanceof Error ? err.message : 'Unknown error';

		throw new FetchError(`GraphQL query failed (promise rejected): ${originalErrorMessage}`, {
			errorCode: 'performGqlFetchError',
		});
	}
};

type GraphqlApiError = {
	message: string;
	status_code: number;
	classification: string;
};

export class GqlApiError extends Error implements RovoInstrumentedError {
	message: string;
	errorAttributes: RovoInstrumentedError['errorAttributes'];

	constructor({ errors, queryName }: { errors: GraphqlApiError[]; queryName?: string }) {
		const errorsString = errors.map((error) => error.message).join(', ');
		const errorMessage = `GraphQL API error: ${errorsString}`;

		super(errorMessage);

		this.message = errorMessage;
		this.name = 'GraphQLError';
		this.errorAttributes = {
			errorCode: 'GqlApiError',
			queryName,
			rawErrorMessage: errorMessage,
			errors: errorsString,
		};
	}
}

const processGqlErrorResponse = async ({
	errors,
	gqlQuery,
}: {
	errors: GqlError[];
	gqlQuery: string;
}) => {
	const errorsToThrow: GraphqlApiError[] = [];

	errors.forEach((error) => {
		errorsToThrow.push({
			message: error.message,
			status_code: error.extensions.statusCode,
			classification: error.extensions.classification,
		});
	});

	throw new GqlApiError({
		errors: errorsToThrow,
		queryName: extractQueryName(gqlQuery),
	});
};
